import { useState, useEffect, useContext, useMemo } from "react";
import { useStyle } from "../../context/styleContext/styleContext";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import {
  Box,
  Typography,
  Button,
  Divider,
  Paper,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import Seo from "../../components/Seo";

export default function ShoppingCart() {
  const { style } = useStyle();
  const navigate = useNavigate();
  const { cartItems, removeFromCart } = useContext(ShoppingCartContext);
  const { getCartPricing, loadingPromotions } = usePromotions();

  const [taxTotal] = useState(0);
  const [height, setHeight] = useState(window.innerHeight);
  const [useAutomaticBestDeal, setUseAutomaticBestDeal] = useState(true);
  const [manualSelectedPromotionIds, setManualSelectedPromotionIds] = useState([]);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    window.scrollTo({ top: 0, behavior: "auto" });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const truncateText = (text, maxLength = 120) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trimEnd() + "...";
  };

  const cartPricing = useMemo(() => {
    return getCartPricing(cartItems, {
      useAutomaticBestDeal,
      selectedPromotionIds: manualSelectedPromotionIds,
    });
  }, [cartItems, getCartPricing, useAutomaticBestDeal, manualSelectedPromotionIds]);

  const effectiveSelectedPromotionIds = useAutomaticBestDeal
    ? cartPricing.selectedPromotionIds || []
    : manualSelectedPromotionIds;

  const availablePromotions = cartPricing.availablePromotions || [];
  const hasAvailablePromotions = availablePromotions.length > 0;

  const selectedPromotionObjects = availablePromotions.filter((promotion) =>
    effectiveSelectedPromotionIds.includes(promotion._id)
  );

  const hasSelectedNonCombinablePromotion = selectedPromotionObjects.some(
    (promotion) => !promotion.canCombine
  );

  const isPromotionDisabled = (promotion) => {
    const isSelected = effectiveSelectedPromotionIds.includes(promotion._id);
    if (isSelected) return false;

    if (!hasSelectedNonCombinablePromotion && promotion.canCombine) {
      return false;
    }

    if (hasSelectedNonCombinablePromotion) {
      return true;
    }

    if (!promotion.canCombine && selectedPromotionObjects.length > 0) {
      return true;
    }

    return false;
  };

  const originalSubtotal = cartPricing.originalSubtotal;
  const promotionsDiscountTotal = cartPricing.promotionsDiscountTotal;
  const subtotalAfterDiscounts = cartPricing.finalSubtotal;
  const total = useMemo(
    () => subtotalAfterDiscounts + taxTotal,
    [subtotalAfterDiscounts, taxTotal]
  );

  const navToCheckout = () => {
    navigate("/checkout");
  };

  const deleteFromCart = (sku) => {
    removeFromCart(sku);
  };

  const togglePromotion = (promotionId) => {
    setManualSelectedPromotionIds((prev) => {
      if (prev.includes(promotionId)) {
        return prev.filter((id) => id !== promotionId);
      }
      return [...prev, promotionId];
    });
  };

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";
  const panelBg2 = style.darkerPanel || "rgba(6, 9, 14, 0.55)";

  const processSteps = [
    {
      title: "Complete checkout",
      text: "Pay securely using the checkout page.",
      icon: (
        <ShoppingCartRoundedIcon
          sx={{ color: style.accentGreen || style.color4 }}
        />
      ),
    },
    {
      title: "Download your plugin",
      text: "Your software will be available from My Gear and the product page.",
      icon: (
        <DownloadRoundedIcon
          sx={{ color: style.accentGreen || style.color4 }}
        />
      ),
    },
    {
      title: "Sign in to unlock",
      text: "Open the plugin in your DAW and sign in with your Parish Audio account.",
      icon: (
        <LockOpenRoundedIcon
          sx={{ color: style.accentGreen || style.color4 }}
        />
      ),
    },
  ];

  const emptyCart = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "20px",
        overflow: "hidden",
        backgroundColor: style.color1,
        border: `1px solid ${style.color2}`,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 5 },
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src="/shopping_cart.svg"
          alt="Empty shopping cart"
          sx={{ width: 180, maxWidth: "80%", opacity: 0.95, mb: 2 }}
        />

        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "1.9rem", md: "2.3rem" },
            fontWeight: 800,
            mb: 1,
            color: style.color4,
            letterSpacing: "-0.03em",
          }}
        >
          Your cart is empty
        </Typography>

        <Typography
          sx={{
            color: style.color3,
            lineHeight: 1.75,
            maxWidth: 620,
            mx: "auto",
          }}
        >
          Browse the plugin lineup and add the tools you want to your cart.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mt: 3 }}
        >
          <Button
            component={RouterLink}
            to="/plugins"
            variant="contained"
            sx={{
              borderRadius: "5px",
              backgroundColor: style.accentGreen || style.color2,
              color: style.color4,
              fontWeight: 700,
              px: 2.5,
              py: 1.1,
              "&:hover": {
                backgroundColor: style.accentGreen || style.color2,
                filter: "brightness(1.05)",
              },
            }}
          >
            Browse Plugins
          </Button>

          <Button
            component={RouterLink}
            to="/"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              borderRadius: "5px",
              borderColor: "rgba(255,255,255,0.12)",
              color: style.color4,
              fontWeight: 700,
              px: 2.5,
              py: 1.1,
              "&:hover": {
                borderColor: "rgba(255,255,255,0.18)",
                backgroundColor: "rgba(255,255,255,0.04)",
              },
            }}
          >
            Back to Home
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  const cartItemCard = (line) => {
    const item = line.item;

    return (
      <Paper
        key={item.SKU}
        elevation={0}
        sx={{
          borderRadius: "14px",
          backgroundColor: panelBg,
          border: "1px solid rgba(255,255,255,0.08)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "100px 1fr", sm: "130px 1fr" },
            minHeight: { xs: 136, sm: 154 },
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1.25,
            }}
          >
            <Box
              component="a"
              href={`/products/${item.productURI}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                component="img"
                src={item.images?.[0]?.URI}
                alt={item.images?.[0]?.altText || item.name}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 116,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "flex-start" }}
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="a"
                    href={`/products/${item.productURI}`}
                    sx={{
                      display: "inline-block",
                      fontSize: { xs: "1.05rem", sm: "1.2rem" },
                      fontWeight: 800,
                      color: style.color4,
                      textDecoration: "none",
                      lineHeight: 1.25,
                      "&:hover": { color: style.color3 },
                    }}
                  >
                    {item.name}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.75,
                      color: style.color3,
                      lineHeight: 1.65,
                      fontSize: "0.93rem",
                    }}
                  >
                    {truncateText(
                      item.shortDescription ||
                        item.cardSummary ||
                        "Audio plugin download",
                      120
                    )}
                  </Typography>

                  {line.appliedPromotions?.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      {line.promotionLabels.map((label, index) => (
                        <Chip
                          key={`${line.lineId}-${label}-${index}`}
                          size="small"
                          icon={<LocalOfferRoundedIcon />}
                          label={label}
                          sx={{
                            color: style.color4,
                            backgroundColor: "rgba(80,180,120,0.22)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "5px",
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Stack
                  alignItems={{ xs: "flex-start", sm: "flex-end" }}
                  spacing={0.25}
                >
                  {line.lineDiscount > 0 ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          color: style.color3,
                          textDecoration: "line-through",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ${line.originalLineTotal.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.2rem",
                          fontWeight: 800,
                          color: style.color4,
                          whiteSpace: "nowrap",
                        }}
                      >
                        ${line.discountedLineTotal.toFixed(2)}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: 800,
                        color: style.color4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      ${Number(item.price).toFixed(2)}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                Instant digital download
              </Typography>

              <IconButton
                onClick={() => deleteFromCart(item.SKU)}
                aria-label={`Remove ${item.name} from cart`}
                sx={{
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Paper>
    );
  };

  const summaryCard = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        backgroundColor: panelBg,
        border: "1px solid rgba(255,255,255,0.08)",
        position: { md: "sticky" },
        top: { md: 90 },
      }}
    >
      <Typography
        sx={{
          color: style.color4,
          fontSize: "1.18rem",
          fontWeight: 800,
          mb: 1.5,
        }}
      >
        Order Summary
      </Typography>

      {loadingPromotions ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={16} sx={{ color: style.color4 }} />
          <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
            Checking promotions...
          </Typography>
        </Stack>
      ) : null}

      {hasAvailablePromotions && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 1.15,
              mb: 1.5,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <AutoAwesomeRoundedIcon
                sx={{
                  mt: "1px",
                  fontSize: 18,
                  color: style.accentGreen || style.color4,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  sx={{ color: style.color4, fontWeight: 700, fontSize: "0.9rem" }}
                >
                  Best deals automatically applied
                </Typography>
                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: "0.85rem",
                    lineHeight: 1.55,
                    mt: 0.2,
                  }}
                >
                  We automatically apply the promotions that provide you the biggest savings and the lowest price.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* <FormControlLabel
            control={
              <Checkbox
                checked={useAutomaticBestDeal}
                onChange={(e) => setUseAutomaticBestDeal(e.target.checked)}
                sx={{
                  color: style.color3,
                  alignSelf: "center",
                  "&.Mui-checked": {
                    color: style.accentGreen,
                  },
                }}
              />
            }
            label="Use best deal automatically"
            sx={{
              mb: 1,
              color: style.color4,
              alignItems: "center",
              minHeight: 36,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.9rem",
                lineHeight: 1.35,
                display: "flex",
                alignItems: "center",
              },
            }}
          /> */}
        </>
      )}

      {/* {!useAutomaticBestDeal && hasAvailablePromotions && (
        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography sx={{ color: style.color4, fontWeight: 700, fontSize: "0.9rem" }}>
            Choose promotions
          </Typography>

          {availablePromotions.map((promotion) => (
            <FormControlLabel
            key={promotion._id}
            disabled={isPromotionDisabled(promotion)}
            control={
              <Checkbox
                checked={effectiveSelectedPromotionIds.includes(promotion._id)}
                onChange={() => togglePromotion(promotion._id)}
                sx={{
                  color: style.color3,
                  alignSelf: "center",
                  "&.Mui-checked": {
                    color: style.accentGreen,
                  },
                  "&.Mui-disabled": {
                    color: style.color2,
                  },
                }}
              />
            }
            label={promotion.bannerText || promotion.title}
            sx={{
              alignItems: "center",
              m: 0,
              minHeight: 36,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.87rem",
                lineHeight: 1.35,
                color: style.color3,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiFormControlLabel-label.Mui-disabled": {
                color: style.color2,
              },
            }}
          />
          ))}
        </Stack>
      )} */}

      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: style.color3 }}>Subtotal</Typography>
          <Typography sx={{ color: style.color4, fontWeight: 700 }}>
            ${originalSubtotal.toFixed(2)}
          </Typography>
        </Stack>

        {promotionsDiscountTotal > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: style.color3 }}>Promotions</Typography>
            <Typography sx={{ color: style.accentGreen, fontWeight: 700 }}>
              -${promotionsDiscountTotal.toFixed(2)}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: style.color3 }}>Sales Tax</Typography>
          <Typography sx={{ color: style.color4, fontWeight: 700 }}>
            ${taxTotal.toFixed(2)}
          </Typography>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.75, borderColor: "rgba(255,255,255,0.08)" }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          sx={{
            color: style.color4,
            fontWeight: 800,
            fontSize: "1.02rem",
          }}
        >
          Total
        </Typography>
        <Typography
          sx={{
            color: style.color4,
            fontWeight: 900,
            fontSize: "1.55rem",
          }}
        >
          ${total.toFixed(2)}
        </Typography>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        onClick={navToCheckout}
        endIcon={<KeyboardArrowRightRoundedIcon />}
        sx={{
          mt: 2.25,
          borderRadius: "5px",
          backgroundColor: style.accentGreen || style.color2,
          color: style.color4,
          fontWeight: 800,
          py: 1.15,
          fontSize: "1rem",
          "&:hover": {
            backgroundColor: style.accentGreen || style.color2,
            filter: "brightness(1.05)",
          },
        }}
      >
        Proceed to Checkout
      </Button>

      {cartPricing.appliedPromotions?.length > 0 && (
        <>
          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }} />
          <Stack spacing={0.8}>
            <Typography sx={{ fontWeight: 700, color: style.color4 }}>
              Applied Promotions
            </Typography>

            {cartPricing.appliedPromotions.map((promotion) => (
              <Stack
                key={promotion._id}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <LocalOfferRoundedIcon
                  sx={{ color: style.accentGreen || style.color4, fontSize: 16 }}
                />
                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: "0.88rem",
                    lineHeight: 1.5,
                  }}
                >
                  {promotion.bannerText || promotion.title}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      <Divider sx={{ my: 1.75, borderColor: "rgba(255,255,255,0.08)" }} />

      <Stack spacing={1.1}>
        <Typography sx={{ fontWeight: 700, color: style.color4 }}>
          What happens after checkout?
        </Typography>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleRoundedIcon
            sx={{
              color: style.accentGreen || style.color4,
              fontSize: "1.05rem",
              mt: "2px",
            }}
          />
          <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
            Download your plugin installer from the product page or your{" "}
            <RouterLink
              to="/my-gear"
              style={{ color: style.accentGreen || style.color4 }}
            >
              My Gear
            </RouterLink>{" "}
            page.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleRoundedIcon
            sx={{
              color: style.accentGreen || style.color4,
              fontSize: "1.05rem",
              mt: "2px",
            }}
          />
          <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
            Run the installer to install and configure your plugin.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <CheckCircleRoundedIcon
            sx={{
              color: style.accentGreen || style.color4,
              fontSize: "1.05rem",
              mt: "2px",
            }}
          />
          <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
            Open the plugin in your DAW and sign in with your Parish Audio account.
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );

  const processCard = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.25 },
        borderRadius: "14px",
        backgroundColor: panelBg,
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: 800,
              color: style.color4,
              mb: 0.75,
            }}
          >
            How it works
          </Typography>

          <Typography sx={{ color: style.color3, lineHeight: 1.75, maxWidth: 780 }}>
            Buying and using your plugin is simple. After checkout, you download it,
            install it, and sign in to unlock it.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 1.25,
          }}
        >
          {processSteps.map((step) => (
            <Paper
              key={step.title}
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: panelBg2,
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Stack spacing={1}>
                {step.icon}
                <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    color: style.color3,
                    lineHeight: 1.65,
                    fontSize: "0.92rem",
                  }}
                >
                  {step.text}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <>
      <Seo
        title="Shopping Cart | Parish Audio"
        description="Review the plugins in your cart and continue to secure checkout on Parish Audio."
        canonical="https://parishaudio.com/shopping-cart"
        robots="noindex,follow"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "clip",
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
          minHeight: height - 160,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120 }}>
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                pt: 2,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 3,
                  mt: 1,
                  width: "95%",
                  margin: "auto",
                  minHeight: { xs: 50, sm: 80, md: 100 },
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "black",
                  backgroundImage: `
                    linear-gradient(
                      90deg,
                      rgba(8, 12, 18, 0.78) 0%,
                      rgba(8, 12, 18, 0.72) 38%,
                      rgba(8, 12, 18, 0.52) 68%,
                      rgba(8, 12, 18, 0.38) 100%
                    ),
                    url('/assets/HeroBanner1.png')
                  `,
                  backgroundSize: "cover",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.42) 100%)",
                  }}
                />

                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    px: { xs: 2, md: 4 },
                    py: { xs: 0.75, md: 0.8 },
                  }}
                >
                  <Stack spacing={1.25} alignItems="center" textAlign="center">
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: { xs: "1.55rem", md: "2rem" },
                        fontWeight: 800,
                        lineHeight: 1.08,
                        letterSpacing: "-0.03em",
                        maxWidth: 680,
                      }}
                    >
                      Review Your Cart
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 3 } }}>
                {cartItems.length < 1 ? (
                  emptyCart
                ) : (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "minmax(0,1.2fr) minmax(320px,0.8fr)",
                        },
                        gap: 2,
                        alignItems: "start",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 2, md: 2.25 },
                            borderRadius: "14px",
                            backgroundColor: panelBg,
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Chip
                              label={`${cartItems.length} item${
                                cartItems.length === 1 ? "" : "s"
                              }`}
                              sx={{
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "5px",
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        </Paper>

                        {cartPricing.lineItems.map(cartItemCard)}

                        <Button
                          component={RouterLink}
                          to="/plugins"
                          startIcon={<ArrowBackRoundedIcon />}
                          variant="text"
                          sx={{
                            alignSelf: "flex-start",
                            color: style.color3,
                            fontWeight: 700,
                            px: 0.5,
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: style.color4,
                            },
                          }}
                        >
                          Continue Shopping
                        </Button>
                      </Stack>

                      {summaryCard}
                    </Box>

                    {processCard}
                  </Stack>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}


// export default ShoppingCart;

// import { useState, useEffect, useContext, useMemo } from "react";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { useNavigate, Link as RouterLink } from "react-router-dom";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { usePromotions } from "../../context/promotionsContext/promotionsContext";
// import {
//   Box,
//   Typography,
//   Button,
//   Divider,
//   Paper,
//   Stack,
//   Chip,
//   IconButton,
//   CircularProgress,
// } from "@mui/material";
// import DeleteIcon from "@mui/icons-material/Delete";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
// import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
// import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
// import Seo from "../../components/Seo";

// export default function ShoppingCart() {
//   const { style } = useStyle();
//   const navigate = useNavigate();
//   const { cartItems, removeFromCart } = useContext(ShoppingCartContext);
//   const { getCartPricing, loadingPromotions } = usePromotions();

//   const [taxTotal] = useState(0);
//   const [height, setHeight] = useState(window.innerHeight);

//   useEffect(() => {
//     const handleResize = () => setHeight(window.innerHeight);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const cartPricing = useMemo(() => {
//     return getCartPricing(cartItems);
//   }, [cartItems, getCartPricing]);

//   const originalSubtotal = cartPricing.originalSubtotal;
//   const promotionsDiscountTotal = cartPricing.promotionsDiscountTotal;
//   const subtotalAfterDiscounts = cartPricing.finalSubtotal;
//   const total = useMemo(
//     () => subtotalAfterDiscounts + taxTotal,
//     [subtotalAfterDiscounts, taxTotal]
//   );

//   const navToCheckout = () => {
//     navigate("/checkout");
//   };

//   const deleteFromCart = (sku) => {
//     removeFromCart(sku);
//   };

//   const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";
//   const panelBg2 = style.darkerPanel || "rgba(6, 9, 14, 0.55)";

//   const processSteps = [
//     {
//       title: "Complete checkout",
//       text: "Pay securely, powered by Stripe.",
//       icon: (
//         <ShoppingCartRoundedIcon
//           sx={{ color: style.accentGreen || style.color4 }}
//         />
//       ),
//     },
//     {
//       title: "Download your plugin",
//       text: "Your software will be available for download instantly.",
//       icon: (
//         <DownloadRoundedIcon
//           sx={{ color: style.accentGreen || style.color4 }}
//         />
//       ),
//     },
//     {
//       title: "Sign in to unlock",
//       text: "Open the plugin in your DAW and sign in with your Parish Audio account.",
//       icon: (
//         <LockOpenRoundedIcon
//           sx={{ color: style.accentGreen || style.color4 }}
//         />
//       ),
//     },
//   ];

//   const emptyCart = (
//     <Paper
//       elevation={0}
//       sx={{
//         borderRadius: "20px",
//         overflow: "hidden",
//         backgroundColor: style.color1,
//         border: `1px solid ${style.color2}`,
//       }}
//     >
//       <Box
//         sx={{
//           px: { xs: 2, md: 3 },
//           py: { xs: 4, md: 5 },
//           textAlign: "center",
//         }}
//       >
//         <Box
//           component="img"
//           src="/shopping_cart.svg"
//           alt="Empty shopping cart"
//           sx={{ width: 180, maxWidth: "80%", opacity: 0.95, mb: 2 }}
//         />

//         <Typography
//           variant="h1"
//           sx={{
//             fontSize: { xs: "1.9rem", md: "2.3rem" },
//             fontWeight: 800,
//             mb: 1,
//             color: style.color4,
//             letterSpacing: "-0.03em",
//           }}
//         >
//           Your cart is empty
//         </Typography>

//         <Typography
//           sx={{
//             color: style.color3,
//             lineHeight: 1.75,
//             maxWidth: 620,
//             mx: "auto",
//           }}
//         >
//           Browse the plugin lineup and add the tools you want to your cart.
//         </Typography>

//         <Stack
//           direction={{ xs: "column", sm: "row" }}
//           spacing={1.5}
//           justifyContent="center"
//           sx={{ mt: 3 }}
//         >
//           <Button
//             component={RouterLink}
//             to="/plugins"
//             variant="contained"
//             sx={{
//               borderRadius: "5px",
//               backgroundColor: style.accentGreen || style.color2,
//               color: style.color4,
//               fontWeight: 700,
//               px: 2.5,
//               py: 1.1,
//               "&:hover": {
//                 backgroundColor: style.accentGreen || style.color2,
//                 filter: "brightness(1.05)",
//               },
//             }}
//           >
//             Browse Plugins
//           </Button>

//           <Button
//             component={RouterLink}
//             to="/"
//             variant="outlined"
//             startIcon={<ArrowBackRoundedIcon />}
//             sx={{
//               borderRadius: "5px",
//               borderColor: "rgba(255,255,255,0.12)",
//               color: style.color4,
//               fontWeight: 700,
//               px: 2.5,
//               py: 1.1,
//               "&:hover": {
//                 borderColor: "rgba(255,255,255,0.18)",
//                 backgroundColor: "rgba(255,255,255,0.04)",
//               },
//             }}
//           >
//             Back to Home
//           </Button>
//         </Stack>
//       </Box>
//     </Paper>
//   );

//   const cartItemCard = (line) => {
//     const item = line.item;

//     return (
//       <Paper
//         key={item.SKU}
//         elevation={0}
//         sx={{
//           borderRadius: "14px",
//           backgroundColor: panelBg,
//           border: "1px solid rgba(255,255,255,0.08)",
//           overflow: "hidden",
//         }}
//       >
//         <Box
//           sx={{
//             display: "grid",
//             gridTemplateColumns: { xs: "100px 1fr", sm: "130px 1fr" },
//             minHeight: { xs: 136, sm: 154 },
//           }}
//         >
//           <Box
//             sx={{
//               backgroundColor: "rgba(255,255,255,0.03)",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               p: 1.25,
//             }}
//           >
//             <Box
//               component="a"
//               href={`/products/${item.productURI}`}
//               sx={{
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 width: "100%",
//                 height: "100%",
//               }}
//             >
//               <Box
//                 component="img"
//                 src={item.images?.[0]?.URI}
//                 alt={item.images?.[0]?.altText || item.name}
//                 sx={{
//                   maxWidth: "100%",
//                   maxHeight: 116,
//                   objectFit: "contain",
//                   display: "block",
//                 }}
//               />
//             </Box>
//           </Box>

//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               justifyContent: "space-between",
//               p: { xs: 1.5, sm: 2 },
//             }}
//           >
//             <Box>
//               <Stack
//                 direction={{ xs: "column", sm: "row" }}
//                 justifyContent="space-between"
//                 alignItems={{ xs: "flex-start", sm: "flex-start" }}
//                 spacing={1}
//               >
//                 <Box sx={{ minWidth: 0 }}>
//                   <Typography
//                     component="a"
//                     href={`/products/${item.productURI}`}
//                     sx={{
//                       display: "inline-block",
//                       fontSize: { xs: "1.05rem", sm: "1.2rem" },
//                       fontWeight: 800,
//                       color: style.color4,
//                       textDecoration: "none",
//                       lineHeight: 1.25,
//                       "&:hover": { color: style.color3 },
//                     }}
//                   >
//                     {item.name}
//                   </Typography>

//                   <Typography
//                     sx={{
//                       mt: 0.75,
//                       color: style.color3,
//                       lineHeight: 1.65,
//                       fontSize: "0.93rem",
//                     }}
//                   >
//                     {item.shortDescription ||
//                       item.cardSummary ||
//                       "Audio plugin download"}
//                   </Typography>

//                 </Box>
//                 <Stack
//                   alignItems={{ xs: "flex-start", sm: "flex-end" }}
//                   spacing={0.25}
//                 >
//                   <Typography
//                     sx={{
//                       fontSize: "1.2rem",
//                       fontWeight: 800,
//                       color: style.color4,
//                       whiteSpace: "nowrap",
//                     }}
//                   >
//                     ${Number(item.price).toFixed(2)}
//                   </Typography>
//                 </Stack>
//               </Stack>
//             </Box>

//             <Stack
//               direction="row"
//               justifyContent="space-between"
//               alignItems="center"
//               sx={{ mt: 2 }}
//             >
//               <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
//                 Instant digital download
//               </Typography>

//               <IconButton
//                 onClick={() => deleteFromCart(item.SKU)}
//                 aria-label={`Remove ${item.name} from cart`}
//                 sx={{
//                   color: style.color4,
//                   backgroundColor: "rgba(255,255,255,0.04)",
//                   "&:hover": {
//                     backgroundColor: "rgba(255,255,255,0.08)",
//                   },
//                 }}
//               >
//                 <DeleteIcon />
//               </IconButton>
//             </Stack>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   };

//   const summaryCard = (
//     <Paper
//       elevation={0}
//       sx={{
//         p: 2,
//         borderRadius: "14px",
//         backgroundColor: panelBg,
//         border: "1px solid rgba(255,255,255,0.08)",
//         position: { md: "sticky" },
//         top: { md: 90 },
//       }}
//     >
//       <Typography
//         sx={{
//           color: style.color4,
//           fontSize: "1.18rem",
//           fontWeight: 800,
//           mb: 1.5,
//         }}
//       >
//         Order Summary
//       </Typography>

//       {loadingPromotions ? (
//         <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
//           <CircularProgress size={16} sx={{ color: style.color4 }} />
//           <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
//             Checking promotions...
//           </Typography>
//         </Stack>
//       ) : null}

//       <Stack spacing={1}>
//         <Stack direction="row" justifyContent="space-between">
//           <Typography sx={{ color: style.color3 }}>Subtotal</Typography>
//           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//             ${originalSubtotal.toFixed(2)}
//           </Typography>
//         </Stack>

//         {promotionsDiscountTotal > 0 && (
//           <Stack direction="row" justifyContent="space-between">
//             <Typography sx={{ color: style.color3 }}>Promotions</Typography>
//             <Typography sx={{ color: style.accentGreen, fontWeight: 700 }}>
//               -${promotionsDiscountTotal.toFixed(2)}
//             </Typography>
//           </Stack>
//         )}

//         <Stack direction="row" justifyContent="space-between">
//           <Typography sx={{ color: style.color3 }}>Sales Tax</Typography>
//           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//             ${taxTotal.toFixed(2)}
//           </Typography>
//         </Stack>
//       </Stack>

//       <Divider sx={{ my: 1.75, borderColor: "rgba(255,255,255,0.08)" }} />

//       <Stack direction="row" justifyContent="space-between" alignItems="center">
//         <Typography
//           sx={{
//             color: style.color4,
//             fontWeight: 800,
//             fontSize: "1.02rem",
//           }}
//         >
//           Total
//         </Typography>
//         <Typography
//           sx={{
//             color: style.color4,
//             fontWeight: 900,
//             fontSize: "1.55rem",
//           }}
//         >
//           ${total.toFixed(2)}
//         </Typography>
//       </Stack>

//       <Button
//         fullWidth
//         variant="contained"
//         onClick={navToCheckout}
//         endIcon={<KeyboardArrowRightRoundedIcon />}
//         sx={{
//           mt: 2.25,
//           borderRadius: "5px",
//           backgroundColor: style.accentGreen || style.color2,
//           color: style.color4,
//           fontWeight: 800,
//           py: 1.15,
//           fontSize: "1rem",
//           "&:hover": {
//             backgroundColor: style.accentGreen || style.color2,
//             filter: "brightness(1.05)",
//           },
//         }}
//       >
//         Proceed to Checkout
//       </Button>

//       <Typography
//         sx={{
//           mt: 1.25,
//           color: style.color3,
//           fontSize: "0.9rem",
//           lineHeight: 1.65,
//           textAlign: "center",
//         }}
//       >
//         Secure checkout. Your plugin will be available after purchase is confirmed.
//       </Typography>

//       {cartPricing.appliedPromotions?.length > 0 && (
//         <>
//           <Divider sx={{ my: 1.75, borderColor: "rgba(255,255,255,0.08)" }} />
//           <Stack spacing={0.8}>
//             <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//               Applied Promotions
//             </Typography>

//             {cartPricing.appliedPromotions.map((promotion) => (
//               <Stack
//                 key={promotion._id}
//                 direction="row"
//                 spacing={1}
//                 alignItems="center"
//               >
//                 <LocalOfferRoundedIcon
//                   sx={{ color: style.accentGreen || style.color4, fontSize: 16 }}
//                 />
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     fontSize: "0.88rem",
//                     lineHeight: 1.5,
//                   }}
//                 >
//                   {promotion.bannerText || promotion.title}
//                 </Typography>
//               </Stack>
//             ))}
//           </Stack>
//         </>
//       )}

//       <Divider sx={{ my: 1.75, borderColor: "rgba(255,255,255,0.08)" }} />

//       <Stack spacing={1.1}>
//         <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//           What happens after checkout?
//         </Typography>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon
//             sx={{
//               color: style.accentGreen || style.color4,
//               fontSize: "1.05rem",
//               mt: "2px",
//             }}
//           />
//           <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
//             Download your plugin from the product page or your{" "}
//             <RouterLink
//               to="/my-gear"
//               style={{ color: style.accentGreen || style.color4 }}
//             >
//               My Gear
//             </RouterLink>{" "}
//             page.
//           </Typography>
//         </Stack>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon
//             sx={{
//               color: style.accentGreen || style.color4,
//               fontSize: "1.05rem",
//               mt: "2px",
//             }}
//           />
//           <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
//             Run the installer to install and configure your plugin.
//           </Typography>
//         </Stack>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon
//             sx={{
//               color: style.accentGreen || style.color4,
//               fontSize: "1.05rem",
//               mt: "2px",
//             }}
//           />
//           <Typography sx={{ color: style.color3, fontSize: "0.9rem", lineHeight: 1.6 }}>
//             Open the plugin in your DAW and sign in with your Parish Audio account.
//           </Typography>
//         </Stack>
//       </Stack>
//     </Paper>
//   );

//   const processCard = (
//     <Paper
//       elevation={0}
//       sx={{
//         p: { xs: 2, md: 2.25 },
//         borderRadius: "14px",
//         backgroundColor: panelBg,
//         border: "1px solid rgba(255,255,255,0.08)",
//       }}
//     >
//       <Stack spacing={2}>
//         <Box>
//           <Typography
//             sx={{
//               fontSize: "1.2rem",
//               fontWeight: 800,
//               color: style.color4,
//               mb: 0.75,
//             }}
//           >
//             How it works
//           </Typography>

//           <Typography sx={{ color: style.color3, lineHeight: 1.75, maxWidth: 780 }}>
//             Buying and using your plugin is simple. After checkout, you download it,
//             install it, and sign in to unlock it.
//           </Typography>
//         </Box>

//         <Box
//           sx={{
//             display: "grid",
//             gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
//             gap: 1.25,
//           }}
//         >
//           {processSteps.map((step) => (
//             <Paper
//               key={step.title}
//               elevation={0}
//               sx={{
//                 p: 1.5,
//                 borderRadius: "10px",
//                 backgroundColor: panelBg2,
//                 border: "1px solid rgba(255,255,255,0.06)",
//               }}
//             >
//               <Stack spacing={1}>
//                 {step.icon}
//                 <Typography sx={{ fontWeight: 800, color: style.color4 }}>
//                   {step.title}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     lineHeight: 1.65,
//                     fontSize: "0.92rem",
//                   }}
//                 >
//                   {step.text}
//                 </Typography>
//               </Stack>
//             </Paper>
//           ))}
//         </Box>
//       </Stack>
//     </Paper>
//   );

//   return (
//     <>
//       <Seo
//         title="Shopping Cart | Parish Audio"
//         description="Review the plugins in your cart and continue to secure checkout on Parish Audio."
//         canonical="https://parishaudio.com/shopping-cart"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "clip",
//           px: { xs: 1.5, md: 2.5 },
//           py: { xs: 2, md: 3 },
//           display: "flex",
//           justifyContent: "center",
//           boxSizing: "border-box",
//           minHeight: height - 160,
//         }}
//       >
//         <Box sx={{ width: "100%", maxWidth: 1120 }}>
//           <Stack spacing={2}>
//             <Paper
//               elevation={0}
//               sx={{
//                 borderRadius: "20px",
//                 overflow: "hidden",
//                 backgroundColor: style.color1,
//                 border: `1px solid ${style.color2}`,
//                 pt: 2,
//               }}
//             >
//               <Box
//                 sx={{
//                   position: "relative",
//                   overflow: "hidden",
//                   borderRadius: 3,
//                   mt: 1,
//                   width: "95%",
//                   margin: "auto",
//                   minHeight: { xs: 50, sm: 80, md: 100 },
//                   display: "flex",
//                   alignItems: "center",
//                   backgroundColor: "black",
//                   backgroundImage: `
//                     linear-gradient(
//                       90deg,
//                       rgba(8, 12, 18, 0.78) 0%,
//                       rgba(8, 12, 18, 0.72) 38%,
//                       rgba(8, 12, 18, 0.52) 68%,
//                       rgba(8, 12, 18, 0.38) 100%
//                     ),
//                     url('/assets/HeroBanner1.png')
//                   `,
//                   backgroundSize: "cover",
//                   backgroundPosition: "center center",
//                   backgroundRepeat: "no-repeat",
//                 }}
//               >
//                 <Box
//                   sx={{
//                     position: "absolute",
//                     inset: 0,
//                     background:
//                       "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.42) 100%)",
//                   }}
//                 />

//                 <Box
//                   sx={{
//                     position: "relative",
//                     zIndex: 1,
//                     width: "100%",
//                     px: { xs: 2, md: 4 },
//                     py: { xs: 0.75, md: 0.8 },
//                   }}
//                 >
//                   <Stack spacing={1.25} alignItems="center" textAlign="center">
//                     <Typography
//                       sx={{
//                         color: "white",
//                         fontSize: { xs: "1.55rem", md: "2rem" },
//                         fontWeight: 800,
//                         lineHeight: 1.08,
//                         letterSpacing: "-0.03em",
//                         maxWidth: 680,
//                       }}
//                     >
//                       Review Your Cart
//                     </Typography>
//                   </Stack>
//                 </Box>
//               </Box>

//               <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 3 } }}>
//                 {cartItems.length < 1 ? (
//                   emptyCart
//                 ) : (
//                   <Stack spacing={2}>
//                     <Box
//                       sx={{
//                         display: "grid",
//                         gridTemplateColumns: {
//                           xs: "1fr",
//                           md: "minmax(0,1.2fr) minmax(320px,0.8fr)",
//                         },
//                         gap: 2,
//                         alignItems: "start",
//                       }}
//                     >
//                       <Stack spacing={1.5}>
//                         <Paper
//                           elevation={0}
//                           sx={{
//                             p: { xs: 2, md: 2.25 },
//                             borderRadius: "14px",
//                             backgroundColor: panelBg,
//                             border: "1px solid rgba(255,255,255,0.08)",
//                           }}
//                         >
//                           <Stack
//                             direction={{ xs: "column", sm: "row" }}
//                             spacing={1}
//                             justifyContent="space-between"
//                             alignItems={{ xs: "flex-start", sm: "center" }}
//                           >
//                             <Chip
//                               label={`${cartItems.length} item${
//                                 cartItems.length === 1 ? "" : "s"
//                               }`}
//                               sx={{
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.08)",
//                                 borderRadius: "5px",
//                                 fontWeight: 700,
//                               }}
//                             />
//                           </Stack>
//                         </Paper>

//                         {cartPricing.lineItems.map(cartItemCard)}

//                         <Button
//                           component={RouterLink}
//                           to="/plugins"
//                           startIcon={<ArrowBackRoundedIcon />}
//                           variant="text"
//                           sx={{
//                             alignSelf: "flex-start",
//                             color: style.color3,
//                             fontWeight: 700,
//                             px: 0.5,
//                             "&:hover": {
//                               backgroundColor: "transparent",
//                               color: style.color4,
//                             },
//                           }}
//                         >
//                           Continue Shopping
//                         </Button>
//                       </Stack>

//                       {summaryCard}
//                     </Box>

//                     {processCard}
//                   </Stack>
//                 )}
//               </Box>
//             </Paper>
//           </Stack>
//         </Box>
//       </Box>
//     </>
//   );
// }

// import { useState, useEffect, useContext, useMemo } from "react";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { useNavigate, Link as RouterLink } from "react-router-dom";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import {
//   Box,
//   Typography,
//   Button,
//   Divider,
//   useMediaQuery,
//   Card,
//   CardContent,
//   IconButton,
//   Paper,
//   Stack,
//   Grid,
//   Chip,
// } from "@mui/material";
// import DeleteIcon from "@mui/icons-material/Delete";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
// import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
// import Seo from "../../components/Seo";

// export default function ShoppingCart() {
//   const { style } = useStyle();
//   const navigate = useNavigate();
//   const { cartItems, cartSubtotal, removeFromCart } = useContext(ShoppingCartContext);
//   const [taxTotal, setTaxTotal] = useState(0);
//   const [height, setHeight] = useState(window.innerHeight);
//   const isMobile = useMediaQuery("(max-width:900px)");

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const total = useMemo(() => cartSubtotal + taxTotal, [cartSubtotal, taxTotal]);

//   const navToCheckout = () => {
//     navigate("/checkout");
//   };

//   const deleteFromCart = (sku) => {
//     removeFromCart(sku);
//   };

//   const processSteps = [
//     {
//       title: "Complete checkout",
//       text: "Purchase your plugin securely using our checkout page.",
//       icon: <ShoppingCartRoundedIcon sx={{ color: style.color2 }} />,
//     },
//     {
//       title: "Download your plugin",
//       text: "After checkout, your plugin will be available from the product page and your My Gear page.",
//       icon: <DownloadRoundedIcon sx={{ color: style.color2 }} />,
//     },
//     {
//       title: "Sign in to unlock",
//       text: "Open the plugin in your DAW and enter your username and password to unlock it.",
//       icon: <LockOpenRoundedIcon sx={{ color: style.color2 }} />,
//     },
//   ];

//   const emptyCart = (
//     <Paper
//       sx={{
//         p: { xs: 3, md: 5 },
//         textAlign: "center",
//         backgroundColor: style.color1,
//         color: style.color4,
//         borderRadius: 3,
//         boxShadow: 10,
//       }}
//     >
//       <Box
//         component="img"
//         src="/shopping_cart.svg"
//         alt="Empty shopping cart"
//         sx={{ width: 180, maxWidth: "80%", opacity: 0.95, mb: 2 }}
//       />

//       <Typography
//         variant="h1"
//         sx={{
//           fontSize: { xs: "1.8rem", md: "2.2rem" },
//           fontWeight: 800,
//           mb: 1,
//           color: style.color4,
//         }}
//       >
//         Your cart is empty
//       </Typography>

//       <Stack
//         direction={{ xs: "column", sm: "row" }}
//         spacing={1.5}
//         justifyContent="center"
//         sx={{ mt: 3 }}
//       >
//         <Button
//           component={RouterLink}
//           to="/plugins"
//           variant="contained"
//           sx={{
//             backgroundColor: style.color2,
//             color: style.color4,
//             fontWeight: 700,
//             px: 2.5,
//             py: 1.1,
//             "&:hover": {
//               backgroundColor: style.color2,
//               filter: "brightness(1.08)",
//             },
//           }}
//         >
//           Browse Plugins
//         </Button>

//         <Button
//           component={RouterLink}
//           to="/"
//           variant="outlined"
//           startIcon={<ArrowBackRoundedIcon />}
//           sx={{
//             borderColor: style.color2,
//             color: style.color4,
//             fontWeight: 700,
//             px: 2.5,
//             py: 1.1,
//             "&:hover": {
//               borderColor: style.color4,
//               backgroundColor: "rgba(255,255,255,0.04)",
//             },
//           }}
//         >
//           Back to Home
//         </Button>
//       </Stack>
//     </Paper>
//   );

//   const cartItemCard = (item) => (
//     <Card
//       key={item.SKU}
//       sx={{
//         p: 0,
//         borderRadius: 3,
//         overflow: "hidden",
//         backgroundColor: style.color1,
//         color: style.color4,
//         boxShadow: 8,
//       }}
//     >
//       <Box
//         sx={{
//           display: "grid",
//           gridTemplateColumns: { xs: "110px 1fr", sm: "140px 1fr" },
//           minHeight: { xs: 140, sm: 160 },
//         }}
//       >
//         <Box
//           sx={{
//             backgroundColor: "rgba(255,255,255,0.03)",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             p: 1.25,
//           }}
//         >
//           <Box
//             component="a"
//             href={`/products/${item.productURI}`}
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               width: "100%",
//               height: "100%",
//             }}
//           >
//             <Box
//               component="img"
//               src={item.images?.[0]?.URI}
//               alt={item.images?.[0]?.altText || item.name}
//               sx={{
//                 maxWidth: "100%",
//                 maxHeight: 120,
//                 objectFit: "contain",
//                 display: "block",
//               }}
//             />
//           </Box>
//         </Box>

//         <CardContent
//           sx={{
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "space-between",
//             p: { xs: 1.5, sm: 2 },
//             "&:last-child": { pb: { xs: 1.5, sm: 2 } },
//           }}
//         >
//           <Box>
//             <Stack
//               direction={{ xs: "column", sm: "row" }}
//               justifyContent="space-between"
//               alignItems={{ xs: "flex-start", sm: "flex-start" }}
//               spacing={1}
//             >
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   component="a"
//                   href={`/products/${item.productURI}`}
//                   sx={{
//                     display: "inline-block",
//                     fontSize: { xs: "1.1rem", sm: "1.25rem" },
//                     fontWeight: 800,
//                     color: style.color4,
//                     textDecoration: "none",
//                     "&:hover": { color: style.color3 },
//                   }}
//                 >
//                   {item.name}
//                 </Typography>

//                 <Typography
//                   sx={{
//                     mt: 0.75,
//                     color: style.color3,
//                     lineHeight: 1.65,
//                     fontSize: "0.95rem",
//                   }}
//                 >
//                   {item.shortDescription || item.cardSummary || "Audio plugin download"}
//                 </Typography>
//               </Box>

//               <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.75}>
              
//                 <Typography
//                   sx={{
//                     fontSize: "1.25rem",
//                     fontWeight: 800,
//                     color: style.color4,
//                     whiteSpace: "nowrap",
//                   }}
//                 >
//                   ${Number(item.price).toFixed(2)}
//                 </Typography>
//               </Stack>
//             </Stack>
//           </Box>

//           <Stack
//             direction="row"
//             justifyContent="space-between"
//             alignItems="center"
//             sx={{ mt: 2 }}
//           >
//             <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//               Instant digital download
//             </Typography>

//             <IconButton
//               onClick={() => deleteFromCart(item.SKU)}
//               aria-label={`Remove ${item.name} from cart`}
//               sx={{
//                 color: style.color4,
//                 backgroundColor: "rgba(255,255,255,0.04)",
//                 "&:hover": {
//                   backgroundColor: "rgba(255,255,255,0.08)",
//                 },
//               }}
//             >
//               <DeleteIcon />
//             </IconButton>
//           </Stack>
//         </CardContent>
//       </Box>
//     </Card>
//   );

//   const summaryCard = (
//     <Paper
//       sx={{
//         p: 2.5,
//         borderRadius: 3,
//         backgroundColor: style.color1,
//         color: style.color4,
//         boxShadow: 10,
//         position: { md: "sticky" },
//         top: { md: 90 },
//       }}
//     >
//       <Typography
//         variant="h2"
//         sx={{
//           fontSize: "1.35rem",
//           fontWeight: 800,
//           mb: 2,
//           color: style.color4,
//         }}
//       >
//         Order Summary
//       </Typography>

//       <Stack spacing={1.25}>
//         <Stack direction="row" justifyContent="space-between">
//           <Typography sx={{ color: style.color3 }}>Subtotal</Typography>
//           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//             ${cartSubtotal.toFixed(2)}
//           </Typography>
//         </Stack>

//         <Stack direction="row" justifyContent="space-between">
//           <Typography sx={{ color: style.color3 }}>Sales Tax</Typography>
//           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//             ${taxTotal.toFixed(2)}
//           </Typography>
//         </Stack>
//       </Stack>

//       <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

//       <Stack direction="row" justifyContent="space-between" alignItems="center">
//         <Typography sx={{ fontSize: "1.05rem", color: style.color4, fontWeight: 800 }}>
//           Total
//         </Typography>
//         <Typography sx={{ fontSize: "1.5rem", color: style.color4, fontWeight: 900 }}>
//           ${total.toFixed(2)}
//         </Typography>
//       </Stack>

//       <Button
//         fullWidth
//         variant="contained"
//         onClick={navToCheckout}
//         endIcon={<KeyboardArrowRightRoundedIcon />}
//         sx={{
//           mt: 2.5,
//           backgroundColor: style.color2,
//           color: style.color4,
//           fontWeight: 800,
//           py: 1.25,
//           fontSize: "1rem",
//           boxShadow: 8,
//           "&:hover": {
//             backgroundColor: style.color2,
//             filter: "brightness(1.08)",
//           },
//         }}
//       >
//         Proceed to Checkout
//       </Button>

//       <Typography
//         sx={{
//           mt: 1.5,
//           color: style.color3,
//           fontSize: "0.92rem",
//           lineHeight: 1.7,
//           textAlign: "center",
//         }}
//       >
//         Secure checkout. Your plugin will be available to download after purchase.
//       </Typography>

//       <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

//       <Stack spacing={1.1}>
//         <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//           What happens after checkout?
//         </Typography>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//           <Typography sx={{ color: style.color3, fontSize: "0.93rem", lineHeight: 1.65 }}>
//             Download your plugin installer from the product page or your{" "}
//             <RouterLink to="/my-gear" style={{ color: style.color2 }}>
//               My Gear
//             </RouterLink>{" "}
//             page.
//           </Typography>
//         </Stack>
//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//           <Typography sx={{ color: style.color3, fontSize: "0.93rem", lineHeight: 1.65 }}>
//             Run the installer to install and configure your plugin.
//           </Typography>
//         </Stack>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//           <Typography sx={{ color: style.color3, fontSize: "0.93rem", lineHeight: 1.65 }}>
//             Open the plugin in your DAW, and sign in using your email and password (the same that you use for this site).
//           </Typography>
//         </Stack>

//         <Stack direction="row" spacing={1} alignItems="flex-start">
//           <CheckCircleRoundedIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//           <Typography sx={{ color: style.color3, fontSize: "0.93rem", lineHeight: 1.65 }}>
//             Enjoy!
//           </Typography>
//         </Stack>

//       </Stack>
//     </Paper>
//   );

//   const processCard = (
//     <Paper
//       sx={{
//         mt: 4,
//         p: { xs: 2, md: 3 },
//         borderRadius: 3,
//         backgroundColor: style.color1,
//         color: style.color4,
//         boxShadow: 8,
//       }}
//     >
//       <Stack spacing={2}>
//         <Box>
//           <Typography
//             variant="h2"
//             sx={{
//               fontSize: "1.4rem",
//               fontWeight: 800,
//               color: style.color4,
//               mb: 1,
//             }}
//           >
//             How it works
//           </Typography>

//           <Typography sx={{ color: style.color3, lineHeight: 1.8, maxWidth: 780 }}>
//             Buying and using your plugin is simple. After checkout, you download it,
//             install it, and sign in to unlock it.
//           </Typography>
//         </Box>

//         <Grid container spacing={2}>
//           {processSteps.map((step) => (
//             <Grid item xs={12} md={4} key={step.title} sx={{ display: "flex" }}>
//               <Paper
//                 sx={{
//                   p: 2,
//                   borderRadius: 2.5,
//                   backgroundColor: "rgba(255,255,255,0.035)",
//                   boxShadow: 3,
//                   width: "100%",
//                 }}
//               >
//                 <Stack spacing={1}>
//                   {step.icon}
//                   <Typography sx={{ fontWeight: 800, color: style.color4 }}>
//                     {step.title}
//                   </Typography>
//                   <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.95rem" }}>
//                     {step.text}
//                   </Typography>
//                 </Stack>
//               </Paper>
//             </Grid>
//           ))}
//         </Grid>

//         <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
//       </Stack>
//     </Paper>
//   );

//   return (
//     <>
//       <Seo
//         title="Shopping Cart | Parish Audio"
//         description="Review the plugins in your cart and continue to secure checkout on Parish Audio."
//         canonical="https://parishaudio.com/shopping-cart"
//         robots="noindex,follow"
//       />

//       <Box sx={{ width: "95%", maxWidth: 1160, margin: "auto", minHeight: height - 160, py: 4 }}>
//         <Stack spacing={3}>
//           <Box>
//             <Typography
//               variant="h1"
//               sx={{
//                 fontSize: { xs: "2rem", md: "2.7rem" },
//                 mb: 1,
//                 color: style.color4,
//                 fontWeight: 800,
//               }}
//             >
//               Shopping Cart
//             </Typography>

//             {cartItems.length > 0 && (
//               <Typography sx={{ color: style.color3, lineHeight: 1.8, maxWidth: 780 }}>
//                 Review your items below. When you’re ready, continue to checkout to complete
//                 your purchase and unlock your plugins.
//               </Typography>
//             )}
//           </Box>

//           {cartItems.length < 1 ? (
//             emptyCart
//           ) : (
//             <>
//               <Box
//                 sx={{
//                   display: "grid",
//                   gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.4fr) minmax(320px,0.8fr)",
//                   gap: 3,
//                   alignItems: "start",
//                 }}
//               >
//                 <Stack spacing={2}>
//                   {cartItems.map(cartItemCard)}

//                   <Button
//                     component={RouterLink}
//                     to="/plugins"
//                     startIcon={<ArrowBackRoundedIcon />}
//                     variant="text"
//                     sx={{
//                       alignSelf: "flex-start",
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0.5,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Continue Shopping
//                   </Button>
//                 </Stack>

//                 {summaryCard}
//               </Box>

//               {processCard}
//             </>
//           )}
//         </Stack>
//       </Box>
//     </>
//   );
// }
// import { useState, useEffect, useContext } from "react";
// import { useStyle } from '../../context/styleContext/styleContext';
// import { useNavigate, Link } from 'react-router-dom';
// import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
// import {
//   Box,
//   Typography,
//   Button,
//   Divider,
//   useMediaQuery,
//   Card,
//   CardContent,
//   CardMedia,
//   IconButton
// } from "@mui/material";
// import DeleteIcon from '@mui/icons-material/Delete';
// import Seo from "../../components/Seo";


// export default function ShoppingCart() {
//   const { style } = useStyle();
//   const navigate = useNavigate();
//   const { cartItems, cartSubtotal, removeFromCart } = useContext(ShoppingCartContext);
//   const [taxTotal, setTaxTotal] = useState(0);
//   const isMobile = useMediaQuery("(max-width:768px)");


//   const navToCheckout = () => {
//     navigate('/checkout');
//   };

//   const deleteFromCart = (sku) => {
//     removeFromCart(sku);
//   };

//   const renderEmptyCart = () => (
//     <Box sx={{ textAlign: 'center', mt: 8 }}>
//       <Typography variant="h1" sx={{fontSize:'1.4rem', color: style.color4, fontStyle:'bold' }}><strong>Your cart is empty</strong></Typography>
//       <Box component="img" src="/shopping_cart.svg" sx={{ mt: 2, maxWidth: 250, height: 'auto' }} />
//       <Box mt={4}>
//         <Link to="/plugins" style={{ color: style.color4 }}>Return to Shop</Link>
//       </Box>
//       <Box mt={1}>
//         <Link to="/home" style={{ color: style.color4 }}>Home</Link>
//       </Box>
//     </Box>
//   );

//   const renderCartItem = (item) => (
//     <Card key={item.SKU} sx={{ display: 'flex', mb: 2, backgroundColor: style.color1, color: style.color4, borderRadius: 2, boxShadow: 6 }}>
//       <a href={"/products/" + item.productURI}>
//       <CardMedia
//         component="img"
//         sx={{ width: '75%', objectFit: 'contain', p: 1, borderRadius: 2, borderTopRightRadius:0, borderBottomRightRadius:0, margin:'auto' }}
//         image={item.images[0].URI}
//         alt={item.images[0].altText}
//       />
//       </a>
//       <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth:'50%', backgroundColor:style.color2}}>
//         <CardContent>
//         <a href={"/products/" + item.productURI}>
//           <Typography sx={{ fontSize:'1.3rem', color: style.color4, fontStyle:'bold', textAlign:'left' }}>{item.name}</Typography>
//         </a>  
//           <Typography variant="body2" sx={{ mt: 1, fontSize:'1.1rem' }}>${item.price.toFixed(2)}</Typography>
//         </CardContent>
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1, pb: 0, mt:6}}>
//           <IconButton onClick={() => deleteFromCart(item.SKU)} sx={{ color: style.color4 }}>
//             <DeleteIcon />
//           </IconButton>
//         </Box>
//       </Box>
//     </Card>
//   );

//   const renderCartSummary = () => (
//     <Box sx={{ mt: 0, p: 3, backgroundColor: style.color1, borderRadius: 2, boxShadow: 8 }}>
//       <Typography variant="h1" sx={{ mb: 2, fontSize:'1.3rem', fontWeight:'bold' }}>Order Summary</Typography>
//       <Divider sx={{ borderColor: style.color2, mb: 2 }} />
//       <Typography sx={{ mb: 1 }}>Subtotal: <strong>${cartSubtotal.toFixed(2)}</strong></Typography>
//       <Typography sx={{ mb: 1 }}>Sales Tax: <strong>${taxTotal.toFixed(2)}</strong></Typography>
//       <Typography variant="h2" sx={{fontSize:'1.2rem'}}>Total: <strong>${(cartSubtotal + taxTotal).toFixed(2)}</strong></Typography>
//       <Button
//         fullWidth
//         variant="contained"
//         onClick={navToCheckout}
//         sx={{ mt: 3, backgroundColor: style.color1, filter:'brightness(1.1)', borderColor:style.color2, borderWidth:'1px', borderStyle:'solid', boxShadow:10, color: style.color4, '&:hover': { filter:'brightness(1.2)' } }}
//       >
//         Proceed to Checkout
//       </Button>
//     </Box>
//   );

//   return (

//     <>
//     <Seo
//       title="Shopping Cart | Parish Audio"
//       description="Review the plugins in your cart and continue to checkout on Parish Audio."
//       canonical="https://parishaudio.com/shopping-cart"
//       robots="noindex,follow"
//     />
//     <Box sx={{ width: '95%', maxWidth: 1000, margin: 'auto', minHeight: 'calc(100vh - 160px)', py: 4 }}>
//       <Typography variant="h1" sx={{ fontSize:'1.5rem', mb: 4, color: style.color4, fontWeight:'bold' }}>Shopping Cart</Typography>
//       {cartItems.length < 1 ? renderEmptyCart() : (
//         <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4 }}>
//           <Box sx={{ flex: 2 }}>{cartItems.map(renderCartItem)}</Box>
//           <Box sx={{ flex: 1 }}>{renderCartSummary()}</Box>
//         </Box>
//       )}
//       {cartItems.length > 0 && (
//         <Box sx={{ mt: 6, textAlign: 'center', color: style.color4 }}>
//           <Typography>
//             <strong>
//               After checkout, your downloads will be available on the <Link to='/my-gear' style={{ color: style.color2 }}>My Gear</Link> page.
//             </strong>
//           </Typography>
//         </Box>
//       )}
//     </Box>
//     </>
//   );
// }

// import { useState, useEffect, useContext } from "react";
// import { useStyle } from '../../context/styleContext/styleContext';
// import {useNavigate, Link} from 'react-router-dom';
// import Button from '@mui/material/Button';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import ReactGA from 'react-ga4';
// //import ProductsTable from "../../components/ProductsTable/ProductsTable";
// //import ShoppingCartItemRow from "../../components/ShoppingCartItemRow/ShoppingCartItemRow";
// import ShoppingCartTable from "../../components/ShoppingCartTable/ShoppingCartTable";
// import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
// //import CartIcon from '/shoppping-cart.svg'
// import Card from '@mui/material/Card';
// import CardActions from '@mui/material/CardActions';
// import CardContent from '@mui/material/CardContent';
// import CardMedia from '@mui/material/CardMedia';
// import Typography from '@mui/material/Typography';

// export default function ShoppingCart() {
//     const { style, setStyle } = useStyle();
//     const [products, setProducts] = useState([]); // Corrected initialization as an empty array
//     const [productDetails, setProductDetails] = useState([]);
//     const navigate = useNavigate()
//     const { cartItems, cartSubtotal, removeFromCart } = useContext(ShoppingCartContext);
//     const [taxTotal, setTaxTotal] = useState(0);

//     const [width, setWidth] = useState(window.innerWidth)
//     const [height, setHeight] = useState(window.innerHeight)

//     function handleWindowSizeChange() {
//         setWidth(window.innerWidth);
//         setHeight(window.innerHeight);
//     }
//     useEffect(() => {
//         window.addEventListener('resize', handleWindowSizeChange);
//         return () => {
//             window.removeEventListener('resize', handleWindowSizeChange);
//         }
//     }, []);

//     const deleteFromCart = async (productSKU) => {


//       console.log("Sending request to cart componant to DELETE..", productSKU)
  
//       removeFromCart(productSKU)  
//   }
    
//     const isMobile = width <= 768;
//     //const isMobile = true;

//     // Google Analytics pageview tracking
//     ReactGA.send({
//         hitType: "pageview",
//         page: "shopping_cart",
//         title: "shopping_cart",
//     });

//     // Set the document title when the page loads
//     useEffect(() => {
//         document.title = 'Parish Audio | Cutting-Edge Audio Processing | Shopping Cart';
//     }, []);

//     const navToCheckout = () => {

//       navigate('/checkout')
//     }


//     return (
//         <>
//         <div style={{height:'100%', minHeight:height - 130, margin:'auto'}}>


        
        
//             {cartItems.length < 1 &&(<>
//                     <div style={{textAlign:'center', margin:'auto', color:style.color4}}><h1 style={{paddingTop:'40px'}}>Your cart Is empty</h1></div>
//                     <div style={{textAlign:'center', margin:'auto'}}>

//                     <img src='/shopping_cart.svg' alt='Light Blue shopping cart' style={{flex:1,height:'250px', maxWidth:'100%', maxHeight:'100%', margin:'auto', textAlign:'center', marginTop:'30px', marginBottom:'15px', paddingRight:'15px'}} />
//                     </div>
//                     <div style={{textAlign:'center', marginTop:'30px'}}><a href={"/plugins"} style={{color:style.color4}}>Return to Shop</a></div>
//                     <div style={{textAlign:'center', marginTop:'15px'}}><a href={"/home"} style={{color:style.color4}}>Home</a></div>
//                     </>
//                   )}


//                 {/* -----------------   mobile --------------- */}


//                 {cartItems.length >= 1 && isMobile &&(<>
//                   <div style={{ display:'flex', flexDirection:'column', width:'95%', margin:'auto'}}>
//                 <div style={{display:'flex', flexDirection:'row', width:'95%', maxWidth:'1100px', margin:'auto', marginTop:'30px'}}>
//                 <h1 style={{fontSize:"18pt"}}>Shopping Cart:</h1>
//               </div>


//               <div style={{}}>              
//               {cartItems.map((item) => (


//                   <div key={item.SKU} style={{ margin:'auto', marginBottom:'15px',display:'flex', minHeight:'200px', backgroundColor:'rgb(25,31,34)', flexDirection:'row', width:'100%', borderColor:style.color3, borderWidth:'1px', borderStyle:'solid', borderRadius:'5px'}}>

//                                 <div style={{ textAlign:'center', padding:'15px', backgroundColor:'rgb(25,31,34)', margin:'auto', height:'100%'}}>
//                                               <a href={"/product/" + item.productURI}>
//                                               <img src={item.imageURIs[0]} style={{width:'75px', margin:'auto'}} />
//                                               </a>
//                                 </div>
//                                 <Card sx={{ backgroundColor:style.color2, color:style.color7, flex:1}}>

//                                           <CardContent>
//                                             <Typography gutterBottom variant="h5" component="div">
//                                             <a href={"/product/" + item.productURI} style={{color:'white'}}>
//                                               {item.name}
//                                               </a>
//                                             </Typography>
//                                             <Typography variant="body2" sx={{ color: 'text.secondary', color:style.color7}}>
//                                             <h3><strong>${item.price} </strong> </h3>
//                                             </Typography>
//                                           </CardContent>
//                                           <CardActions>
//                                              <Button onClick={() => deleteFromCart(item.SKU)} size="small">Remove From Cart</Button>
                                            
//                                           </CardActions>
//                                         </Card>

//                                 </div>

                                
                  
                  
                  
                  

//   ))}
              

//               </div>


                            
                       
//                         <div style={{flex:1}}>
//                             <table style={{margin:'auto', width:'100%', borderColor:style.color4, borderThickness:'1px', borderStyle:'solid', borderRadius:'5px', padding:'20px'}}>
//                               <tbody>
              
                              
//                             <tr>
//                               <td style={{width:'125px'}}><h3 style={{padding:'5px', margin:'0px'}}>Subtotal:</h3></td>
//                               <td><h3 style={{ padding:'5px', margin:'0px', textAlign:'center'}}>$ {parseFloat(cartSubtotal).toFixed(2)}</h3></td>
//                             </tr>
//                             <tr>
//                               <td><h3 style={{padding:'5px', margin:'0px'}}>Sales Tax</h3></td>
//                               <td><h3 style={{padding:'5px', margin:'0px', textAlign:'center'}}>$ {parseFloat(taxTotal).toFixed(2)}</h3></td>
              
//                             </tr>
//                             <tr>
//                               <td><h2 style={{padding:'5px', margin:'0px'}}><strong>TOTAL</strong></h2></td>
//                               <td><h2 style={{padding:'5px', margin:'0px', textAlign:'center'}}><strong>$ {parseFloat(cartSubtotal + taxTotal).toFixed(2)} </strong></h2></td>
              
//                             </tr>
//                             </tbody>
//                           </table>
              
//                           <Button onClick={navToCheckout} sx={{width:'100%', backgroundColor:style.color4, color:'white', marginTop:'25px','&:hover': {backgroundColor: style.color1 }}}> Proceed To Checkout </Button>   </div>
              

  
              
//               <div style={{width:'95%', maxWidth:'800px', margin:'auto', marginTop:'75px'}}>
//                 <p><strong>Once your order is complete, you can download your products from your <a href='/my-gear' style={{color:style.color4}}>gear</a> page</strong></p>
//               </div>
//               </div>
              
//               </>)}      

//                 {/* -----------------   desktop   --------------- */}

//               {cartItems.length >= 1 && !isMobile &&(<>
//                 <div style={{display:'flex', flexDirection:'row', width:'95%', maxWidth:'1100px', margin:'auto', marginTop:'30px'}}>
//                 <h1 style={{fontSize:"18pt"}}>shopping cart:</h1>
//               </div>

//                 <div style={{display:'flex', flexDirection:'row', width:'95%', maxWidth:'1100px', margin:'auto'}}>
                            
//                             <div style={{ textAlign: 'center', color: style.color4, width: '95%', maxWidth: '1000px', margin: 'auto'}}>
                                
//                                 <div style={{maxWidth:'1000px', margin:'auto'}}>
//                                   <ShoppingCartTable />
//                                 </div>  
//                             </div>
                       
//                         <div style={{flex:1, marginLeft:'10px'}}>
//                             <table style={{margin:'auto', width:'95%', borderColor:style.color4, borderThickness:'1px', borderStyle:'solid'}}>
//                               <tbody>
              
                              
//                             <tr>
//                               <td style={{width:'125px'}}><h3 style={{padding:'5px', margin:'0px'}}>Subtotal:</h3></td>
//                               <td><h3 style={{width:'125px', padding:'5px', margin:'0px', textAlign:'center'}}>$ {parseFloat(cartSubtotal).toFixed(2)}</h3></td>
//                             </tr>
//                             <tr>
//                               <td><h3 style={{padding:'5px', margin:'0px'}}>Sales Tax</h3></td>
//                               <td><h3 style={{padding:'5px', margin:'0px', textAlign:'center'}}>$ {parseFloat(taxTotal).toFixed(2)}</h3></td>
              
//                             </tr>
//                             <tr>
//                               <td><h2 style={{padding:'5px', margin:'0px'}}>TOTAL</h2></td>
//                               <td><h2 style={{padding:'5px', margin:'0px', textAlign:'center'}}>$ {parseFloat(cartSubtotal + taxTotal).toFixed(2)} </h2></td>
              
//                             </tr>
//                             </tbody>
//                           </table>
              
//                           <Button onClick={navToCheckout} sx={{width:'100%', backgroundColor:style.color1, color:'white', marginTop:'25px','&:hover': {backgroundColor: style.color4 }}}> Proceed To Checkout </Button>   </div>
              

//                       </div>    
              
//               <div style={{width:'95%', maxWidth:'800px', margin:'auto', marginTop:'75px'}}>
//                 <p><strong>Once your order is complete, you can download your products from the <a href='/my-gear' style={{color:style.color4}}>my gear</a> page</strong></p>
//               </div>
//               </>)}       
        

//         </div>

//         </>
//     );
// }








